'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import BrandLogo from '@/components/ui/BrandLogo';

const navLinks = [
  { label: 'Home', href: '/home' },
  { label: 'Destinations', href: '/destinations' },
  { label: 'Packages', href: '/packages' },
  { label: 'Hotels', href: '/hotels' },
  { label: 'Vehicles', href: '/vehicles' },
  { label: 'Guides', href: '/guides' },
];

const moreLinks = [
  { label: '🗺️ Discover', href: '/discover' },
  { label: '🔍 Search', href: '/search' },
  { label: '✈️ Flights', href: '/flights' },
  { label: '🚌 Bus Tickets', href: '/bus-tickets' },
  { label: '🚂 Train Tickets', href: '/train-tickets' },
  { label: '🍽️ Restaurants', href: '/restaurants' },
  { label: '💬 Chat', href: '/chat' },
  { label: '⭐ Reviews', href: '/reviews' },
  { label: '🏆 Leaderboard', href: '/discover/leaderboard' },
  { label: '✈️ Trip Planner', href: '/trip-planner' },
  { label: '📝 Travel Blog', href: '/blog' },
  { label: '🗂️ Collections', href: '/collections' },
  { label: '🎨 Icon Pack', href: '/icon-pack' },
];

const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'ne', label: 'नेपाली', name: 'Nepali' },
  { code: 'hi', label: 'हिंदी', name: 'Hindi' },
  { code: 'zh', label: '中文', name: 'Chinese' },
  { code: 'ja', label: '日本語', name: 'Japanese' },
];

const ROLE_LABELS: Record<string, string> = {
  tourist: 'Tourist',
  guide: 'Guide',
  hotel_owner: 'Hotel Owner',
  vehicle_owner: 'Vehicle Owner',
  agency: 'Agency',
  admin: 'Admin',
};

const PROVIDER_ROLES = ['guide', 'hotel_owner', 'vehicle_owner', 'agency'];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifs, setRecentNotifs] = useState<any[]>([]);
  const router = useRouter();
  const pathname = usePathname();
  const { user, profile, signOut, loading } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setMoreOpen(false);
        setProfileOpen(false);
        setNotifOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const fetchNotifs = useCallback(async () => {
    if (!user) { setUnreadCount(0); setRecentNotifs([]); return; }
    try {
      const supabase = createClient();
      const { data, count } = await supabase
        .from('notifications')
        .select('id, title, body, is_read, created_at', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(5);
      setUnreadCount(count || 0);
      setRecentNotifs(data || []);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifs]);

  const markNotifRead = async (id: string) => {
    try {
      const supabase = createClient();
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      setRecentNotifs(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setProfileOpen(false);
      setMenuOpen(false);
      router.push('/home');
    } catch {}
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email || '';
  const userRole = profile?.role || user?.user_metadata?.role || 'tourist';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  const isProvider = PROVIDER_ROLES.includes(userRole);

  const headerBg = scrolled ? 'backdrop-blur-xl border-b' : 'bg-transparent';
  const headerStyle = scrolled
    ? { background: 'rgba(11,45,92,0.96)', borderColor: 'rgba(244,180,0,0.12)', boxShadow: '0 4px 30px rgba(0,0,0,0.4)' }
    : {};

  const currentLang = LANGUAGES.find(l => l.code === activeLang) || LANGUAGES[0];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
      style={headerStyle}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between gap-4" style={{ height: 'clamp(52px, 8vw, 72px)' }}>
        {/* Logo — slightly larger on mobile, standard on desktop */}
        <BrandLogo size={42} showTagline={false} className="shrink-0" />

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-0.5 text-sm font-medium" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={pathname === link.href ? 'page' : undefined}
              className={`px-3 py-2 rounded-lg transition-all duration-200 ${pathname === link.href ? 'font-semibold' : 'hover:bg-white/5'}`}
              style={{
                color: pathname === link.href ? 'var(--gold-bright)' : 'rgba(245,240,232,0.8)',
                background: pathname === link.href ? 'rgba(244,180,0,0.1)' : undefined,
              }}
            >
              {link.label}
            </Link>
          ))}

          {/* More dropdown */}
          <div className="relative" data-dropdown>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={moreOpen}
              className="px-3 py-2 rounded-lg flex items-center gap-1 transition-all hover:bg-white/5"
              style={{ color: 'rgba(245,240,232,0.8)' }}
            >
              More
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round"/>
              </svg>
            </button>
            {moreOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-52 rounded-2xl shadow-2xl overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #1A3870, #0B2D5C)', border: '1px solid rgba(244,180,0,0.12)', animation: 'fade-scale 0.15s ease forwards' }}
              >
                {moreLinks.map((link) => (
                  <Link key={link.href} href={link.href} onClick={() => setMoreOpen(false)}
                    className="block px-4 py-3 text-sm transition-colors hover:bg-white/5"
                    style={{ color: 'rgba(245,240,232,0.8)' }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          {/* Search */}
          <Link href="/search" className="p-2 rounded-lg transition-colors hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.6)' }} aria-label="Search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
          </Link>

          {/* Language selector */}
          <div className="relative" data-dropdown>
            <button
              onClick={() => { setLangOpen(!langOpen); setNotifOpen(false); setProfileOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
              style={{ color: 'rgba(245,240,232,0.7)', border: '1px solid rgba(245,240,232,0.1)' }}
              aria-label="Language selector"
            >
              🌐 {currentLang.label}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${langOpen ? 'rotate-180' : ''}`}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round"/>
              </svg>
            </button>
            {langOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-40 rounded-2xl shadow-2xl overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #1A3870, #0B2D5C)', border: '1px solid rgba(244,180,0,0.12)', animation: 'fade-scale 0.15s ease forwards' }}
              >
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => { setActiveLang(lang.code); setLangOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors hover:bg-white/5"
                    style={{ color: activeLang === lang.code ? 'var(--gold-bright)' : 'rgba(245,240,232,0.75)' }}
                  >
                    <span>{lang.name}</span>
                    {activeLang === lang.code && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            href="/guides/become-guide"
            className="text-xs font-medium px-3 py-1.5 rounded-full transition-all hover:bg-gold/10"
            style={{ color: 'var(--gold-bright)', border: '1px solid rgba(244,180,0,0.35)' }}
          >
            Become a Guide
          </Link>

          {!loading && (
            <>
              {user ? (
                <>
                  {/* Notifications */}
                  <div className="relative" data-dropdown>
                    <button
                      onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); setLangOpen(false); }}
                      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                      className="relative p-2 rounded-lg transition-colors hover:bg-white/5"
                      style={{ color: 'rgba(245,240,232,0.7)' }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      </svg>
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ background: 'var(--sunrise)' }}>
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                    {notifOpen && (
                      <div
                        className="absolute top-full right-0 mt-2 w-80 rounded-2xl shadow-2xl overflow-hidden"
                        style={{ background: 'linear-gradient(160deg, #1A3870, #0B2D5C)', border: '1px solid rgba(244,180,0,0.12)', animation: 'fade-scale 0.15s ease forwards' }}
                      >
                        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(244,180,0,0.08)' }}>
                          <span className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>Notifications</span>
                          <Link href="/notifications" onClick={() => setNotifOpen(false)} className="text-xs hover:underline" style={{ color: 'var(--glacier-light)' }}>View all</Link>
                        </div>
                        {recentNotifs.length === 0 ? (
                          <div className="px-4 py-8 text-center text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>No new notifications</div>
                        ) : (
                          recentNotifs.map(n => (
                            <button
                              key={n.id}
                              onClick={() => markNotifRead(n.id)}
                              className="w-full text-left px-4 py-3 transition-colors hover:bg-white/5"
                              style={{ borderBottom: '1px solid rgba(244,180,0,0.05)' }}
                            >
                              <div className="text-xs font-medium mb-0.5" style={{ color: 'var(--himal-white)' }}>{n.title}</div>
                              <div className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{n.body}</div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Profile avatar */}
                  <div className="relative" data-dropdown>
                    <button
                      onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); setLangOpen(false); }}
                      className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full transition-all hover:bg-white/5"
                      style={{ border: '1px solid rgba(244,180,0,0.2)' }}
                      aria-label="Profile menu"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg, #F4B400, #E0AA18)', color: '#0B2D5C' }}
                      >
                        {initials}
                      </div>
                      <span className="text-xs font-medium max-w-[80px] truncate" style={{ color: 'rgba(245,240,232,0.8)' }}>
                        {displayName.split(' ')[0]}
                      </span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${profileOpen ? 'rotate-180' : ''}`} style={{ color: 'rgba(245,240,232,0.4)' }}>
                        <path d="M6 9l6 6 6-6" strokeLinecap="round"/>
                      </svg>
                    </button>
                    {profileOpen && (
                      <div
                        className="absolute top-full right-0 mt-2 w-60 rounded-2xl shadow-2xl overflow-hidden"
                        style={{ background: 'linear-gradient(160deg, #1A3870, #0B2D5C)', border: '1px solid rgba(244,180,0,0.12)', animation: 'fade-scale 0.15s ease forwards' }}
                      >
                        <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(244,180,0,0.08)' }}>
                          <div className="font-semibold text-sm" style={{ color: 'var(--himal-white)' }}>{displayName}</div>
                          <div className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.45)' }}>{userEmail}</div>
                          <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(244,180,0,0.12)', color: 'var(--gold-bright)', border: '1px solid rgba(244,180,0,0.2)' }}>
                            {ROLE_LABELS[userRole] || 'Tourist'}
                          </span>
                        </div>
                        {[
                          { label: '👤 My Profile', href: '/profile' },
                          { label: '🗺️ My Trips', href: '/my-trips' },
                          { label: '📋 My Bookings', href: '/booking/my-bookings' },
                          { label: '❤️ Wishlist', href: '/wishlist' },
                          { label: '🗂️ Collections', href: '/collections' },
                          { label: '🗺️ Saved Trips', href: '/trip-planner' },
                          { label: '📍 My Places', href: '/discover/my-contributions' },
                          { label: '💳 Payments', href: '/payments' },
                          ...(isProvider ? [{ label: '🏢 Provider Dashboard', href: '/provider' }] : []),
                          ...(userRole === 'admin' ? [{ label: '⚙️ Admin Panel', href: '/admin' }] : []),
                          { label: '⚙️ AI Settings', href: '/ai-settings' },
                        ].map(item => (
                          <Link key={item.href} href={item.href} onClick={() => setProfileOpen(false)}
                            className="block px-4 py-2.5 text-sm transition-colors hover:bg-white/5"
                            style={{ color: 'rgba(245,240,232,0.75)' }}
                          >
                            {item.label}
                          </Link>
                        ))}
                        <div style={{ borderTop: '1px solid rgba(244,180,0,0.08)' }}>
                          <button onClick={handleSignOut}
                            className="w-full text-left px-4 py-3 text-sm transition-colors hover:bg-white/5"
                            style={{ color: '#f87171' }}
                          >
                            🚪 Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link href="/login" className="text-sm font-medium px-4 py-2 rounded-xl transition-all hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.8)' }}>
                    Sign In
                  </Link>
                  <Link
                    href="/register"
                    className="text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #F4B400, #E0AA18)', color: '#0B2D5C', boxShadow: '0 4px 12px rgba(244,180,0,0.3)' }}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="xl:hidden rounded-xl transition-all"
          style={{
            width: 44,
            height: 44,
            minWidth: 44,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(245,240,232,0.08)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(245,240,232,0.12)',
            color: 'var(--himal-white)',
          }}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen
              ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
            }
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="xl:hidden border-t"
          style={{
            background: 'rgba(11,45,92,0.98)',
            backdropFilter: 'blur(24px)',
            borderColor: 'rgba(244,180,0,0.1)',
            animation: 'slide-up 0.2s ease forwards',
          }}
        >
          <div className="px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            {[...navLinks, ...moreLinks].map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="block px-4 py-3 rounded-xl text-sm transition-colors hover:bg-white/5"
                style={{ color: pathname === link.href ? 'var(--gold-bright)' : 'rgba(245,240,232,0.8)' }}
              >
                {link.label}
              </Link>
            ))}
            <div style={{ borderTop: '1px solid rgba(244,180,0,0.08)', paddingTop: 12, marginTop: 8 }}>
              {/* Language selector mobile */}
              <div className="flex gap-2 flex-wrap mb-3">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => setActiveLang(lang.code)}
                    className="text-xs px-2.5 py-1 rounded-full transition-all"
                    style={{
                      background: activeLang === lang.code ? 'rgba(244,180,0,0.15)' : 'rgba(245,240,232,0.06)',
                      color: activeLang === lang.code ? 'var(--gold-bright)' : 'rgba(245,240,232,0.6)',
                      border: `1px solid ${activeLang === lang.code ? 'rgba(244,180,0,0.3)' : 'rgba(245,240,232,0.1)'}`,
                    }}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              {user ? (
                <>
                  <div className="px-4 py-3 mb-1 rounded-xl" style={{ background: 'rgba(244,180,0,0.06)', border: '1px solid rgba(244,180,0,0.1)' }}>
                    <div className="font-semibold text-sm" style={{ color: 'var(--himal-white)' }}>{displayName}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.45)' }}>{userEmail}</div>
                  </div>
                  {[
                    { label: '👤 My Profile', href: '/profile' },
                    { label: '🗺️ My Trips', href: '/my-trips' },
                    { label: '📋 My Bookings', href: '/booking/my-bookings' },
                    { label: '❤️ Wishlist', href: '/wishlist' },
                    { label: '🗂️ Collections', href: '/collections' },
                    { label: '💳 Payments', href: '/payments' },
                    ...(isProvider ? [{ label: '🏢 Provider Dashboard', href: '/provider' }] : []),
                    ...(userRole === 'admin' ? [{ label: '⚙️ Admin Panel', href: '/admin' }] : []),
                  ].map(item => (
                    <Link key={item.href} href={item.href}
                      className="block px-4 py-2.5 rounded-xl text-sm transition-colors hover:bg-white/5"
                      style={{ color: 'rgba(245,240,232,0.75)' }}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <button onClick={handleSignOut} className="w-full text-left px-4 py-3 rounded-xl text-sm mt-1" style={{ color: '#f87171', background: 'rgba(239,68,68,0.06)' }}>
                    🚪 Sign Out
                  </button>
                </>
              ) : (
                <div className="flex gap-2">
                  <Link href="/login" className="flex-1 text-center px-4 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(245,240,232,0.06)', color: 'rgba(245,240,232,0.8)' }}>Sign In</Link>
                  <Link href="/register" className="flex-1 text-center px-4 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #F4B400, #E0AA18)', color: '#0B2D5C' }}>Get Started</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}